import type { PressEvent } from '@react-types/shared';
import { FocusEventHandler, HTMLAttributes, MouseEvent, RefObject } from 'react';

import { Optional } from '../../utils/types';
import { FontWeight } from '../../utils/typography';
import { PaletteColor } from '../ThemeProvider';
import {
	FocusRingVariants as _FocusRingVariants,
	FocusRingVariantsType as _FocusRingVariantsType,
} from './focusRingVariants';

export const FocusRingVariants = _FocusRingVariants;
export type FocusRingVariantsType = _FocusRingVariantsType;

export const ButtonSizes = {
	Sm: 'sm',
	Md: 'md',
	Lg: 'lg',
} as const;
export type ButtonSize = (typeof ButtonSizes)[keyof typeof ButtonSizes];

export interface ButtonProps {
	children?: React.ReactNode;
	onPress?: (e: PressEvent) => void;
	onPressStart?: (e: PressEvent) => void;
	onMouseEnter?: HTMLAttributes<HTMLButtonElement>['onMouseEnter'];
	onMouseLeave?: HTMLAttributes<HTMLButtonElement>['onMouseLeave'];
	onKeyDown?: HTMLAttributes<HTMLButtonElement>['onKeyDown'];
	href?: string;
	backgroundColor: PaletteColor;
	borderColor?: PaletteColor;
	className?: string;
	contentAlignment?: 'left' | 'center';
	download?: boolean;
	isDisabled?: boolean;
	Icon?: React.FC;
	iconColor?: PaletteColor;
	maxWidth?: string;
	textColor?: PaletteColor;
	fontWeight?: FontWeight;
	height?: string;
	type?: 'button' | 'submit' | 'reset';
	form?: string;
	isLoading?: boolean;
	fullWidth?: boolean;
	openInNewTab?: boolean;
	loaderColor?: PaletteColor;
	hideShadow?: boolean;
	size?: ButtonSize;
	id?: string;
	role?: string;
	tabIndex?: number;
	buttonRef?: RefObject<HTMLButtonElement | HTMLAnchorElement>;
	onFocus?: FocusEventHandler<Element>;
	onBlur?: FocusEventHandler<Element>;
	/**
    When an element has keyboard focus, we display a focus ring. This helps
    keyboard users determine which element on a page or in an application has
    keyboard focus as they navigate around.
    */
	focusRingVariant?: FocusRingVariantsType;
	/**
	 * Used for Storybook rendering only.
	 */
	UNSAFE_showFocusRing?: boolean;
	/**
	 * @deprecated Please use onPress instead (which does not receive a MouseEvent for accessibility reasons)
	 */
	legacyOnClick?: (e: MouseEvent<HTMLButtonElement>) => void;
}

type AriaLabelProps = {
	'aria-label'?: string;
	'aria-labelledby'?: string;
} & (
	| { 'aria-label': string; 'aria-labelledby'?: never }
	| { 'aria-labelledby': string; 'aria-label'?: never }
	| { 'aria-label'?: never; 'aria-labelledby'?: never }
);

export type OptionalBackgroundButtonProps = Optional<ButtonProps & AriaLabelProps, 'backgroundColor'>;
